import React from "react";
import { Link } from "react-router-dom";
import useSeo from "../hooks/useSeo";
import { Accessibility, CheckCircle2, AlertTriangle, Mail } from "lucide-react";

const Section = ({ id, title, children }) => (
  <div id={id} className="mb-10 scroll-mt-24">
    <h2 className="font-display font-semibold text-2xl text-brand-dark tracking-tight">{title}</h2>
    <div className="mt-3 text-brand-text2 leading-relaxed space-y-3">{children}</div>
  </div>
);

export default function AccessibilityPage() {
  useSeo({
    title: "Tilgængelighedserklæring · EMA Kurer ApS",
    description:
      "EMA Kurer ApS' tilgængelighedserklæring iht. EU Accessibility Act (EAA) 2025 og WCAG 2.1 niveau AA. Læs hvordan vi sikrer at vores hjemmeside er tilgængelig for alle brugere.",
    path: "/tilgaengelighed",
  });

  return (
    <div data-testid="accessibility-page">
      <section className="bg-brand-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red flex items-center gap-2">
            <Accessibility size={14} /> Juridisk
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mt-3">Tilgængelighedserklæring</h1>
          <p className="text-white/70 mt-3">Version 1.0 · Senest opdateret: 10. februar 2026</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <Section id="lovgrundlag" title="1. Lovgrundlag">
          <p>
            Denne erklæring afgives i overensstemmelse med:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>EU Accessibility Act (EAA)</strong> — Europa-Parlamentets og Rådets direktiv (EU) 2019/882 om tilgængelighedskrav for produkter og tjenester, i kraft 28. juni 2025</li>
            <li><strong>Lov om tilgængelighed af offentlige organers websteder og mobilapplikationer</strong> (LBK nr. 692 af 02/07/2018, suppleret af EAA)</li>
            <li><strong>WCAG 2.1 niveau AA</strong> (Web Content Accessibility Guidelines, W3C)</li>
            <li><strong>EN 301 549</strong> (europæisk standard for digital tilgængelighed)</li>
          </ul>
        </Section>

        <Section id="omfang" title="2. Erklæringens omfang">
          <p>
            Denne erklæring gælder for hjemmesiden <strong>emakurer.dk</strong> samt alle underliggende sider og services
            der drives af EMA Kurer ApS — inkl. kunde-booking, chauffør-portal, admin-dashboard og PWA-mobilapper.
          </p>
        </Section>

        <Section id="status" title="3. Overensstemmelsesstatus">
          <div className="bg-emerald-50 border border-emerald-300 p-4 flex items-start gap-3">
            <CheckCircle2 size={20} className="text-emerald-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-emerald-900">Delvist i overensstemmelse</div>
              <div className="text-sm text-emerald-800 mt-1">
                Hjemmesiden er <strong>delvist i overensstemmelse</strong> med WCAG 2.1 niveau AA på grund af de uoverensstemmelser
                der er angivet nedenfor. Vi arbejder løbende på at lukke disse huller inden for de tidsrammer angivet i pkt. 5.
              </div>
            </div>
          </div>
        </Section>

        <Section id="hvad-virker" title="4. Hvad er allerede tilgængeligt">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Semantisk HTML</strong> med korrekte landmarks (header, nav, main, footer)</li>
            <li><strong>Tastaturnavigation</strong> — alle interaktive elementer kan tilgås uden mus</li>
            <li><strong>Synlig fokus-indikator</strong> på alle interaktive elementer</li>
            <li><strong>Kontrastforhold</strong> der opfylder AA-niveau (4.5:1 for normal tekst, 3:1 for store overskrifter)</li>
            <li><strong>Alt-tekst</strong> på alle informative billeder</li>
            <li><strong>Labels</strong> på alle formularfelter</li>
            <li><strong>ARIA-attributter</strong> for dynamiske komponenter (toast-notifikationer, modaler, dropdowns)</li>
            <li><strong>Responsive design</strong> der virker fra 320px op til 4K — også med 200 % zoom</li>
            <li><strong>Skærmlæser-kompatibel</strong> (testet med VoiceOver og NVDA)</li>
            <li><strong>Dansk sprogkode</strong> (lang="da") på alle sider</li>
          </ul>
        </Section>

        <Section id="huller" title="5. Kendte uoverensstemmelser og tidsplan">
          <div className="bg-amber-50 border border-amber-300 p-4 flex items-start gap-3 mb-4">
            <AlertTriangle size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              Følgende punkter er endnu ikke fuldt i overensstemmelse. Hver post har en estimeret tidsramme for udbedring.
            </div>
          </div>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Kort-visning på spore-siden</strong> (/track): Google Maps-integrationen er ikke fuldt skærmlæser-kompatibel.
              Vi tilbyder en alternativ tekst-baseret visning af afhentnings- og leveringsadresser. (<em>Fuldt fix Q3 2026</em>)
            </li>
            <li>
              <strong>QR-kode i bekræftelsesmails</strong>: Vi vedhæfter altid både QR-kode og en tekst-baseret bookingnummer
              som alternativ, så QR ikke er den eneste vej til at identificere pakken. (<em>Aktuelt OK</em>)
            </li>
            <li>
              <strong>Foto-upload (chauffør-app)</strong>: Krævende billed-baserede flows har endnu ikke fuld voice-over support.
              (<em>Fix Q2 2026</em>)
            </li>
            <li>
              <strong>PDF-fakturaer</strong>: Vores genererede PDF-fakturaer er endnu ikke fuldt "tagged PDF" iht. WCAG.
              Vi tilbyder altid en HTML-version af fakturaen som alternativ. (<em>Fix Q4 2026</em>)
            </li>
          </ul>
        </Section>

        <Section id="genveje" title="6. Tastaturgenveje på emakurer.dk">
          <ul className="list-disc pl-6 space-y-1">
            <li><kbd className="bg-slate-200 px-1.5 py-0.5 rounded font-mono text-xs">Tab</kbd> — gå til næste element</li>
            <li><kbd className="bg-slate-200 px-1.5 py-0.5 rounded font-mono text-xs">Shift+Tab</kbd> — gå tilbage</li>
            <li><kbd className="bg-slate-200 px-1.5 py-0.5 rounded font-mono text-xs">Enter</kbd> — aktivér link / send formular</li>
            <li><kbd className="bg-slate-200 px-1.5 py-0.5 rounded font-mono text-xs">Esc</kbd> — luk dialog / modal</li>
            <li>Vi anvender <strong>ingen brugerdefinerede tastaturgenveje</strong> der konflikter med skærmlæsere</li>
          </ul>
        </Section>

        <Section id="kontakt" title="7. Kontakt og klage">
          <p>
            Hvis du oplever en tilgængelighedsbarriere på emakurer.dk, eller hvis du har brug for indhold i et alternativt
            format (fx en HTML-version af en PDF-faktura), er du velkommen til at kontakte os:
          </p>
          <p>
            <strong>EMA Kurer ApS</strong><br />
            Email: <a href="mailto:kontakt@emakurer.dk" className="text-brand-red">kontakt@emakurer.dk</a><br />
            CVR: 46465717
          </p>
          <p>
            Vi besvarer henvendelser om tilgængelighed inden for <strong>7 arbejdsdage</strong>. Hvis du ikke får et
            tilfredsstillende svar, kan du klage til <strong>Digitaliseringsstyrelsen</strong>:
          </p>
          <p>
            <a href="https://digst.dk/digital-service/webtilgaengelighed/klage-over-manglende-webtilgaengelighed/" target="_blank" rel="noreferrer" className="text-brand-red underline">
              digst.dk — klage over manglende webtilgængelighed
            </a>
          </p>
        </Section>

        <Section id="udarbejdelse" title="8. Erklæringens udarbejdelse">
          <p>
            Denne erklæring blev udarbejdet 10. februar 2026 baseret på en intern selvevaluering af emakurer.dk, herunder
            automatiske tests (axe-core, Lighthouse), tastatur-navigations-test og manuel skærmlæser-test (VoiceOver, NVDA).
          </p>
          <p>
            Vi gennemgår tilgængeligheden mindst <strong>én gang om året</strong> og opdaterer denne erklæring tilsvarende.
          </p>
        </Section>

        <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <div className="flex items-start gap-3">
            <Mail size={20} className="text-brand-red shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-lg font-bold text-brand-dark">Hjælp os med at blive bedre</h3>
              <p className="text-sm text-brand-text2 mt-1">
                Vi vil meget gerne høre fra dig hvis noget ikke virker som det skal — send os en mail på{" "}
                <a href="mailto:kontakt@emakurer.dk" className="text-brand-red">kontakt@emakurer.dk</a> med en beskrivelse.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 text-xs text-brand-text2 text-center">
          Se også: <Link to="/privatlivspolitik" className="text-brand-red underline">Privatlivspolitik</Link> ·{" "}
          <Link to="/cookie-politik" className="text-brand-red underline">Cookie-politik</Link> ·{" "}
          <Link to="/handelsbetingelser" className="text-brand-red underline">Handelsbetingelser</Link>
        </div>
      </section>
    </div>
  );
}
